import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { LogOut, Menu } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/lib/i18n";
import { Brand, LangToggle, iconBtnClass } from "@/components/Brand";
import { MobileNavDrawer } from "@/components/MobileNavDrawer";

/** Nav entries depend on the signed-in role. */
const navLinks = ({ user, isOwner, isStaff, isPlatformOwner }, t) => {
  const links = [];
  if (isStaff) {
    links.push({ to: "/dashboard", label: t("projects") });
    links.push({ to: "/analytics", label: t("analytics") });
    if (isOwner) links.push({ to: "/settings", label: t("settings") });
    links.push({ to: "/billing", label: t("billing") });
  }
  if (isPlatformOwner) links.push({ to: "/platform", label: t("platform") });
  if (user?.tenant_role === "client") links.push({ to: "/portal", label: t("my_projects") });
  return links;
};

export const AppHeader = () => {
  const { user, logout, isOwner, isStaff, isPlatformOwner } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const links = navLinks({ user, isOwner, isStaff, isPlatformOwner }, t);

  const signOut = async () => {
    setOpen(false);
    await logout();
    navigate("/login");
  };

  return (
    <>
      <header className="glass sticky top-0 z-40 border-b border-[#2A2A2A]">
      <div className="max-w-7xl mx-auto px-5 lg:px-12 h-16 flex items-center justify-between gap-4">
        <div className="flex items-center gap-8">
          <Brand to={isStaff ? "/dashboard" : "/"} logo={user?.tenant?.logo_url} />
          <nav className="hidden md:flex items-center gap-6">
            {links.map((l) => (
              <Link
                key={l.to}
                data-testid={`nav-${l.to.slice(1)}`}
                to={l.to}
                className={`text-[11px] tracking-[0.18em] uppercase transition-colors ${
                  pathname === l.to ? "text-[#D4AF37]" : "text-[#A8A8A8] hover:text-[#F3F3F1]"
                }`}
              >
                {l.label}
              </Link>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-4">
          <LangToggle className="hidden md:flex" />
          <span data-testid="header-user-email" className="hidden lg:block text-xs text-[#A8A8A8]">
            {user?.email}
          </span>
          <button
            data-testid="logout-btn"
            onClick={signOut}
            className={`${iconBtnClass} hidden md:inline-flex text-[#A8A8A8] hover:text-[#D4AF37]`}
            aria-label={t("logout")}
          >
            <LogOut size={16} />
          </button>
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setOpen(true)}
            className={`${iconBtnClass} md:hidden text-[#F3F3F1] hover:text-[#D4AF37]`}
            aria-label={t("menu")}
            aria-expanded={open}
          >
            <Menu size={22} />
          </button>
        </div>
      </div>

      </header>

      {open && (
        <MobileNavDrawer links={links} pathname={pathname} email={user?.email}
          onClose={() => setOpen(false)} onSignOut={signOut} />
      )}
    </>
  );
};
